import { registerLocaleData } from '@angular/common';
import localeEsMx from '@angular/common/locales/es-MX';
import { bootstrapApplication } from '@angular/platform-browser';
import { initializeSentry } from '@seatix/shared-util-error-tracking';
import { readAuthTransfer } from '@seatix/shared/auth/utils';
import { AppComponent } from './app/app.component';
import { appConfig } from './app/app.config';

// Type definitions for Scheduler API
interface SchedulerTask {
  priority?: 'user-blocking' | 'user-visible' | 'background';
}

interface Scheduler {
  postTask(callback: () => void, options?: SchedulerTask): void;
}

// Type definitions for Tauri
interface TauriWindow {
  __TAURI__?: {
    window: any;
    app: any;
    fs: any;
    http: any;
    dialog: any;
    notification: any;
  };
}

declare global {
  interface Window extends TauriWindow {
    scheduler?: Scheduler;
  }
}

const release = `${process.env['STX_APP_ID']}@${process.env['STX_APP_VERSION']}`;

// Initialize Sentry with Tauri-specific configuration
initializeSentry({
  dsn: process.env['STX_SENTRY_DSN_CONTROL_HUB_DESKTOP'] as string,
  environment: process.env['STX_ENVIRONMENT'] as string,
  release,
  tracesSampleRate: process.env['STX_ENVIRONMENT'] === 'production' ? 0.1 : 1.0,
  debug: process.env['STX_ENVIRONMENT'] === 'development',
  enabled: process.env['STX_ENVIRONMENT'] !== 'development',
  tracePropagationTargets: ['localhost', 'api.seatix.mx'],
  sendDefaultPii: true,
  dist: process.env['STX_BUILD_NUMBER'],
  integrations: { browserTracing: true, replay: true },
});

// Register locale data
registerLocaleData(localeEsMx);

readAuthTransfer();

// Performance optimization: defer non-critical initialization
function initializeApp() {
  bootstrapApplication(AppComponent, appConfig)
    .then((appRef) => {
      // App successfully bootstrapped
      console.log('Seatix Control Hub Desktop initialized');

      // Load deferred styles after app is ready
      import('./app/services/deferred-style-loader.service').then(
        ({ DeferredStyleLoaderService }) => {
          const styleLoader = appRef.injector.get(DeferredStyleLoaderService);
          styleLoader.loadDeferredStyles();
        },
      );

      // Initialize Tauri-specific features if running in Tauri
      if (window.__TAURI__) {
        console.log('Running in Tauri desktop environment');
        // Additional desktop-specific initialization can go here
      }
    })
    .catch((err) => {
      console.error('Error initializing desktop app:', err);
    });
}

// Use scheduler.postTask if available for better performance
if (window.scheduler?.postTask) {
  window.scheduler.postTask(initializeApp, { priority: 'user-blocking' });
} else {
  initializeApp();
}
